import * as THREE from "three";
import { Mesh, Quaternion, Vector3, Vector2, Raycaster } from "three";
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import AppData from "../../../app.data";
import { Utils } from "../../../utils/utils";
import Renderer from "../../../renderer/renderer";
import { createMovedArtworkKey, getActiveArtworkId, isPseudoMoMSpaceData } from "../../../utils/artworkPlacement.utils";

export class EventArtworkManipulator {
    public updatedArtworks: Map<number | string, any>;
    public isArtowrkSelectToggle: boolean = false;
    private selectedArtwork: any = null;
    public transformControls: TransformControls;
    private pedestalTransformControls: TransformControls;
    private utils: Utils;
    private appData: AppData;
    private renderer: Renderer;
    private snapToWallObserverInterval: any = null;

    constructor(renderer: Renderer) {
        this.renderer = renderer;
        this.appData = new AppData();
        this.utils = new Utils();
        this.updatedArtworks = new Map();
    }

    private getSelectedArtworkMoveTarget() {
        if (!this.selectedArtwork) return null;
        const slotTarget = Number(this.selectedArtwork.userData?.index);
        if (!Number.isFinite(slotTarget)) return null;

        const artworkWrapper = this.selectedArtwork.userData?.artworkWrapper;
        const artworkId = isPseudoMoMSpaceData(this.appData.spaceData)
            ? getActiveArtworkId(artworkWrapper)
            : undefined;

        return {
            key: createMovedArtworkKey(slotTarget, artworkId),
            slotTarget,
            artworkId,
            artworkWrapper,
        };
    }

    private buildSelectedArtworkMoveValue(pedestalDiameter: number | null = null) {
        const target = this.getSelectedArtworkMoveTarget();
        if (!target) return null;

        return {
            slotTarget: target.slotTarget,
            ...(target.artworkId ? { artworkId: target.artworkId } : {}),
            position: { x: this.selectedArtwork.position.x, y: this.selectedArtwork.position.y, z: this.selectedArtwork.position.z },
            rotation: { x: this.selectedArtwork.rotation.x, y: this.selectedArtwork.rotation.y, z: this.selectedArtwork.rotation.z },
            scale: { x: this.selectedArtwork.scale.x, y: this.selectedArtwork.scale.y, z: this.selectedArtwork.scale.z },
            pedestalDiameter,
        };
    }

    private resolveSelectedArtworkRoot(selected: Mesh) {
        const parentSlot = this.utils.getSlotObjectBySlotTarget(selected.userData.index, this.appData);
        if (!parentSlot) return selected;

        let current: any = selected;
        while (current?.parent && current.parent !== parentSlot) {
            current = current.parent;
        }

        return current?.parent === parentSlot
            ? current
            : (parentSlot.children?.[0] || selected);
    }

    /**
     * Gets the currently selected artwork
     * @returns The selected artwork object or null if none selected
     */
    public getSelectedArtwork() {
        return this.selectedArtwork;
    }

    /**
     * Enables artwork selection and movement mode
     * Initializes transform controls for artwork manipulation
     */
    public selectAndMoveArtwork = () => {
        this.updatedArtworks.clear();

        this.isArtowrkSelectToggle = true
        this.transformControls = new TransformControls(this.appData.camera, this.renderer.renderer.domElement);

        // FIX 1: Use 'world' space to prevent drag axes from rotating with the object
        // This stops the "fighting" between your mouse drag and the auto-rotation
        this.transformControls.space = "local";

        this.transformControls.showZ = true

        this.transformControls.addEventListener('dragging-changed', () => {
            if (!this.selectedArtwork) return;
            const moveTarget = this.getSelectedArtworkMoveTarget();
            if (!moveTarget) return;

            if (!this.updatedArtworks.get(moveTarget.key)) {
                this.updatedArtworks.set(moveTarget.key, { slotTarget: moveTarget.slotTarget, position: { x: 0, y: 0, z: 0 } });
            }
            // Get pedestal diameter if artwork has pedestal
            let pedestalDiameter = null;
            const artworkWrapper = moveTarget.artworkWrapper;
            if (artworkWrapper?.artworks?.[artworkWrapper.activeArtwork]?.artworkData?.pedestal) {
                pedestalDiameter = artworkWrapper.artworks[artworkWrapper.activeArtwork].artworkData.pedestal.diameter;
            }

            const moveValue = this.buildSelectedArtworkMoveValue(pedestalDiameter);
            if (moveValue) {
                this.updatedArtworks.set(moveTarget.key, moveValue)
            }

            if (this.selectedArtwork.userData.artworkWrapper?.artworks?.[this.selectedArtwork.userData.artworkWrapper.activeArtwork]?.type === "sound") {
                // update sounds in real time
                const soundMoved = this.compareArraysByProperty(moveTarget.slotTarget, this.appData.soundList)
                if (soundMoved) {
                    soundMoved.position.set(
                        this.selectedArtwork.position.x,
                        this.selectedArtwork.position.y,
                        this.selectedArtwork.position.z
                    );
                    soundMoved.updateMatrixWorld(true)
                }
            }
        })

        this.transformControls.addEventListener("change", () => {
            if (!this.selectedArtwork.userData.artworkWrapper) return;
            const activeArtwork = this.selectedArtwork.userData.artworkWrapper.artworks[this.selectedArtwork.userData.artworkWrapper.activeArtwork]
            if (activeArtwork.artworkData.pedestal?.mesh) {
                activeArtwork.artworkData.pedestal.diameter = activeArtwork.artworkData.pedestal.mesh.scale.x
                const slot = this.utils.getSlotObjectBySlotTarget(this.selectedArtwork.userData.index, this.appData)
                this.utils.updatePedestalGeometry(activeArtwork.artworkData.pedestal.mesh, slot, this.selectedArtwork)

            }
        })

        this.transformControls.addEventListener("objectChange", () => {


            if (this.transformControls.dragging && this.selectedArtwork) {
                // FIX: Get world coordinates directly from the object being moved.
                // This ensures valid vectors regardless of TransformControls implementation.
                const worldPosition = new Vector3();
                this.selectedArtwork.getWorldPosition(worldPosition);

                const worldQuaternion = new Quaternion();
                this.selectedArtwork.getWorldQuaternion(worldQuaternion);
                const stored = localStorage.getItem("snapToWall");
                const artworkType = this.selectedArtwork.userData?.artworkWrapper?.artworks?.[this.selectedArtwork.userData?.artworkWrapper?.activeArtwork]?.type
                if (stored === "true" && artworkType !== "3dmodel") {
                    this.computeArtworkPosition(this.selectedArtwork, worldPosition, worldQuaternion)
                }
            }
        })

        // Initialize pedestal transform controls if pedestal exists
        // Create pedestal transform controls
        this.pedestalTransformControls = new TransformControls(this.appData.camera, this.renderer.renderer.domElement);
        this.pedestalTransformControls.space = "local";
        this.pedestalTransformControls.setMode('scale');

        // Lock Y scaling and all translation/rotation
        this.pedestalTransformControls.showX = true;
        this.pedestalTransformControls.showY = false;
        this.pedestalTransformControls.showZ = false;

        // // Attach to pedestal mesh


        // Handle pedestal scaling changes
        this.pedestalTransformControls.addEventListener('dragging-changed', () => {
            const selectedArtowrkSlotIndex = this.selectedArtwork.userData.index
        });

        this.pedestalTransformControls.addEventListener('change', () => {
            if (!this.selectedArtwork.userData.artworkWrapper) return;

            const activeArtwork = this.selectedArtwork.userData.artworkWrapper.artworks[this.selectedArtwork.userData.artworkWrapper.activeArtwork]
            if (!activeArtwork.artworkData.pedestal) return;
            activeArtwork.artworkData.pedestal.diameter = activeArtwork.artworkData.pedestal.mesh.scale.x
            const slot = this.utils.getSlotObjectBySlotTarget(this.selectedArtwork.userData.index, this.appData)
            this.utils.updatePedestalGeometry(activeArtwork.artworkData.pedestal.mesh, slot, this.selectedArtwork)
            // Track pedestal diameter changes for saving
            const moveTarget = this.getSelectedArtworkMoveTarget();
            if (!moveTarget) return;
            if (!this.updatedArtworks.get(moveTarget.key)) {
                const moveValue = this.buildSelectedArtworkMoveValue(activeArtwork.artworkData.pedestal.diameter);
                if (moveValue) {
                    this.updatedArtworks.set(moveTarget.key, moveValue);
                }
            } else {
                // Update existing entry with new pedestal diameter
                const existing = this.updatedArtworks.get(moveTarget.key);
                existing.pedestalDiameter = activeArtwork.artworkData.pedestal.diameter;
                this.updatedArtworks.set(moveTarget.key, existing);
            }

        });

    }

    private computeArtworkPosition(selected: Mesh, worldPosition: Vector3, worldQuaternion: Quaternion) {
        if (!this.appData.camera) return;

        // FIX: Screen-Space Raycasting
        // 1. Project object position to 2D Screen Space (Normalized Device Coordinates)
        const ndc = worldPosition.clone().project(this.appData.camera);

        // 2. Cast ray from Camera through that screen point
        // This finds the surface "under the cursor/object" regardless of the object's local rotation
        const raycaster = new Raycaster();
        raycaster.setFromCamera(new Vector2(ndc.x, ndc.y), this.appData.camera);

        const spaceHolder = this.appData.spaceScene.getObjectByName("sceneHolder");

        // Intersect all walls
        const intersects = raycaster.intersectObjects(spaceHolder.children, true);

        if (intersects.length > 0) {
            // 3. Filter for the best hit
            // We look for the first hit that is actually facing the camera
            let hit = null;
            for (let i = 0; i < intersects.length; i++) {
                if (!intersects[i].face) continue;

                // Check visibility of object and its parents
                let isVisible = true;
                let obj = intersects[i].object;
                while (obj) {
                    if (obj.visible === false) {
                        isVisible = false;
                        break;
                    }
                    obj = obj.parent;
                }
                if (!isVisible) continue;

                // Calculate World Normal of the wall
                const worldNormal = new Vector3();
                worldNormal.copy(intersects[i].face.normal)
                    .transformDirection(intersects[i].object.matrixWorld)
                    .normalize();

                // Check direction: Dot Product < 0 means the wall is facing the camera/ray
                // If > 0, we are hitting the back of a wall (inside), which causes "disappearing"
                if (worldNormal.dot(raycaster.ray.direction) < 0) {
                    hit = intersects[i];
                    break; // Found the closest valid front-face
                }
            }

            if (hit && hit.point) {
                const worldPoint = hit.point.clone();

                // 4. Apply Position
                // Add a tiny offset along the normal to prevent z-fighting (flickering textures)
                const worldNormal = new Vector3();
                worldNormal.copy(hit.face.normal).transformDirection(hit.object.matrixWorld).normalize();
                const safetyOffset = worldNormal.clone().multiplyScalar(0.005);

                selected.position.copy(worldPoint.add(safetyOffset));
                selected.parent.worldToLocal(selected.position); // Preserve hierarchy

                // 5. Apply Rotation
                // Look "away" from the wall (Standard Three.js: Object -Z points to target)
                const lookAtTarget = worldPoint.clone().add(worldNormal);
                selected.lookAt(lookAtTarget);
            }
        }
    }

    private compareArraysByProperty(array1: any, array2: any[]): any | null {
        // const artwork2 = 
        for (const item2 of array2) {
            if (array1 === item2.userData.slotTarget) {
                return item2;
            }
        }
        return null;
    }

    public resetSlotTransforms = (slot: string) => {
        const slotTarget = Number(slot);
        const selectedTarget = this.getSelectedArtworkMoveTarget();
        const useSelectedArtworkTarget = selectedTarget && selectedTarget.slotTarget === slotTarget;
        this.updatedArtworks.set(useSelectedArtworkTarget ? selectedTarget.key : slotTarget, {
            slotTarget,
            ...(useSelectedArtworkTarget && selectedTarget.artworkId ? { artworkId: selectedTarget.artworkId } : {}),
            position: { x: 0, y: 0, z: 0 },
            rotation: { x: 0, y: 0, z: 0 },
            scale: { x: 1, y: 1, z: 1 },
        })
    }

    /**
     * Finalizes artwork movement and returns updated positions
     * @returns Object containing updated artwork positions
     */
    public getMovedArtworks() {
        if (this.snapToWallObserverInterval) {
            clearInterval(this.snapToWallObserverInterval)
            this.snapToWallObserverInterval = null
        }

        if (this.transformControls) {
            this.isArtowrkSelectToggle = false
            this.transformControls.detach()
            this.appData.artworksScene.remove(this.transformControls.getHelper())
            this.transformControls = null
        }

        // Clean up pedestal transform controls
        if (this.pedestalTransformControls) {
            this.pedestalTransformControls.detach()
            this.appData.artworksScene.remove(this.pedestalTransformControls.getHelper())
            this.pedestalTransformControls = null
        }

        return this.updatedArtworks
    }

    /**
     * Attaches transform controls to selected artwork
     * @param selected Selected artwork mesh
     */
    public moveArtwork(selected: Mesh) {
        if (!selected) return

        this.selectedArtwork = this.resolveSelectedArtworkRoot(selected);

        const selectedArtowrk = selected.userData?.artworkWrapper?.artworks?.[selected.userData.artworkWrapper.activeArtwork]

        if (this.transformControls) {
            this.transformControls.attach(this.selectedArtwork)
            this.transformControls.setMode('translate')
            this.appData.artworksScene.add(this.transformControls.getHelper())
            this.appData.renderService.addToRenderQueue();
        }
        // Initialize pedestal transform controls if pedestal exists
        if (this.pedestalTransformControls && selectedArtowrk?.artworkData?.pedestal && selectedArtowrk.artworkData.pedestal.type !== "none") {
            this.pedestalTransformControls.attach(selectedArtowrk.artworkData.pedestal.mesh)
            this.appData.artworksScene.add(this.pedestalTransformControls.getHelper())
            this.appData.renderService.addToRenderQueue();
        }
        // }

        if (this.pedestalTransformControls && (!selectedArtowrk?.artworkData?.pedestal || selectedArtowrk.artworkData.pedestal.type === "none")) {
            // Ensure detached if not needed
            this.pedestalTransformControls.detach()
            this.appData.artworksScene.remove(this.pedestalTransformControls.getHelper())
            this.appData.renderService.addToRenderQueue();
        }
    }
}

